import { useState, useRef, useEffect } from 'react';

import withStyles from '@material-ui/styles/withStyles';
import { Button, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { useTheme, alpha } from '@material-ui/core/styles';
import WarningIcon from '@material-ui/icons/Warning';
import { UserAvatar } from 'altus-ui-components';

import { Plot } from '@int/geotoolkit/plot/Plot';
import { WellBoreNode } from '@int/geotoolkit/schematics/scene/WellBoreNode';
import { WellBoreData } from '@int/geotoolkit/schematics/data/WellBoreData';
import { FillStyle } from '@int/geotoolkit/attributes/FillStyle';
import { ViewMode } from '@int/geotoolkit/schematics/scene/WellBoreNode';
import { Group } from '@int/geotoolkit/scene/Group';
import { Rectangle } from '@int/geotoolkit/scene/shapes/Rectangle';
import { CssLayout } from '@int/geotoolkit/layout/CssLayout';
import { Layer } from '@int/geotoolkit/scene/Layer';
import { Events as SelectionEvents } from '@int/geotoolkit/controls/tools/Selection';
import { ComponentNode } from '@int/geotoolkit/schematics/scene/ComponentNode';
import { LineStyle } from '@int/geotoolkit/attributes/LineStyle';
import { Rect } from '@int/geotoolkit/util/Rect';
import { ComponentNodeFactoryRegistry } from '@int/geotoolkit/schematics/factory/ComponentNodeFactoryRegistry';

import SchematicTable from 'features/wells/schematics/components/SchematicTable';
import SchematicDrawer from 'features/wells/schematics/components/SchematicDrawer';
import SchematicForm from 'features/wells/schematics/components/SchematicForm';
import SchematicModal from 'features/wells/schematics/components/SchematicModal';
import SchematicCommentForm from 'features/wells/schematics/components/SchematicCommentForm';
import SchematicVersionForm from 'features/wells/schematics/components/SchematicVersionForm';

import {
  createWellLogWidget,
  highlightComponentNode,
  initialFormValueMapper,
  leftRectangle,
  rightRectangle,
  setLeftRectangle,
  setRightRectangle,
} from 'features/wells/schematics/helpers/utils';

import 'features/wells/schematics/components/wellBoreStyleLatest.css';

const WellBoreSchematic = ({
  classes,
  comment,
  data,
  versionsList,
  allElements,
  units,
  selectedSchematicVersion,
  isLatestVersion,
  isFetching,
  versionCreator,
  handleSelectChange,
  handleSubmit,
  handleVersionFormSubmit,
  handleDelete,
  handleCancel,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowIndex, setRowIndex] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const canvasRef = useRef();
  const wellBoreNodeRef = useRef();
  const higlightRef = useRef();
  const theme = useTheme();
  const hasData = data !== undefined && data !== null && data.length !== 0;
  const initialValues =
    data && data.length !== 0 && rowIndex !== null
      ? initialFormValueMapper(data[rowIndex])
      : null;
  const white = theme.palette.common.white;
  const grey = theme.palette.tabs.background.main;
  const { length, width } = units;
  const versionsEmpty = versionsList.length === 0;
  const shouldShowButtons = versionsEmpty || editMode;

  const submitCallback = () => {
    setRowIndex(null);
    setIsDrawerOpen(false);
  };

  const deleteCallback = () => {
    setRowIndex(null);
    setIsModalOpen(false);
  };

  const formSubmit = (formState) => {
    handleSubmit(formState, submitCallback, rowIndex);
  };

  const versionFormSubmit = (comment) => {
    handleVersionFormSubmit(comment, () => setEditMode(false));
  };

  const onEdit = (rowIndex) => {
    setRowIndex(rowIndex);
    setIsDrawerOpen(true);
  };

  const onDelete = (rowIndex) => {
    setRowIndex(rowIndex);
    setIsModalOpen(true);
  };

  const onDeleteConfirm = () => {
    handleDelete(rowIndex, deleteCallback);
  };

  const onCancelConfirm = () => {
    setIsModalOpen(false);
    setRowIndex(null);
  };

  const onCancel = () => {
    handleCancel();
    setEditMode(false);
  };

  const onSchematicVersionChange = (event) => {
    setEditMode(false);
    handleSelectChange(event.target.value);
  };

  const handleClick = (index) => {
    if (wellBoreNodeRef.current && higlightRef.current) {
      leftRectangle.setVisible(false);
      rightRectangle.setVisible(false);
      const componentNode = wellBoreNodeRef.current.getComponents().toArray()[
        index
      ];
      highlightComponentNode(
        componentNode,
        wellBoreNodeRef.current,
        higlightRef.current,
      );
    }
  };

  useEffect(() => {
    if (canvasRef.current && data != null) {
      const registry = new ComponentNodeFactoryRegistry(true);
      const wellBoreData = new WellBoreData({
        elements: data,
        units: {
          depth: length,
          value: width,
        },
      });
      const geometryBounds = wellBoreData.getGeometryBounds();
      const wellBoreNode = new WellBoreNode({
        data: wellBoreData,
        viewmode: ViewMode.Regular,
        registry,
      }).setBounds(
        new Rect(0.2, geometryBounds.getTop(), 0.8, geometryBounds.getBottom()),
      );

      wellBoreNodeRef.current = wellBoreNode;
      const wellLogWidget = createWellLogWidget(wellBoreNode, geometryBounds);

      const group = new Group({ automodellimitsmode: false })
        .setLayout(new CssLayout())
        .addChild(
          wellLogWidget.setLayoutStyle({
            top: 0,
            left: 0,
            bottom: 0,
            width: 400,
          }),
        );
      const plot = new Plot({
        canvaselement: canvasRef.current,
        root: group,
      });

      group.setProperties({ border: null });
      group.setProperties({
        linestyle: { color: grey, width: 2 },
      });
      wellBoreNode.setProperties({ border: null });
      wellBoreNode.setProperties({
        linestyle: { color: 'transparent', width: 0 },
      });
      wellLogWidget.fitToHeight();

      const highlight = new Layer();
      higlightRef.current = highlight;

      const highlightLineStyle = new LineStyle('transparent');
      const highlightFillStyle = new FillStyle(alpha(white, 0.36));
      setLeftRectangle(
        new Rectangle({
          linestyle: highlightLineStyle,
          fillstyle: highlightFillStyle,
          visible: false,
        }),
      );
      setRightRectangle(
        new Rectangle({
          linestyle: highlightLineStyle,
          fillstyle: highlightFillStyle,
          visible: false,
        }),
      );
      highlight.addChild([leftRectangle, rightRectangle]);
      wellLogWidget.getTrackContainer().addLayer(highlight);

      const selectionOff = () => {
        leftRectangle.setVisible(false);
        rightRectangle.setVisible(false);
      };

      wellLogWidget
        .getToolByName('pick')
        .setNodeFilter((nodes) =>
          nodes.filter((node) => node instanceof ComponentNode),
        )
        // Add wellbore node click listener:
        .on(SelectionEvents.onPick, (_evt, _sender, eventArgs) => {
          selectionOff();

          // New selection / highlight:
          const nodes = eventArgs.getSelection();
          if (nodes && nodes.length > 0) {
            const componentNode = nodes[nodes.length - 1];
            // Highlight schematics node:
            highlightComponentNode(componentNode, wellBoreNode, highlight);
          }
        });

      return () => {
        plot.dispose();
      };
    }
  }, [data, white, grey, length, width]);

  return (
    <main>
      <Grid item xs={3} style={{ minWidth: '350px' }}>
        {hasData ? (
          <canvas
            ref={canvasRef}
            width="350px"
            height="700px"
            style={{ border: 'none' }}
          />
        ) : (
          <Grid container className={classes.emptyStateWrapper}>
            <WarningIcon
              className={`${classes.emptyStateColor} ${classes.warningIcon}`}
            />
            <Typography variant="body1" className={classes.emptyStateColor}>
              No Schematic version is added yet
            </Typography>
          </Grid>
        )}
      </Grid>
      <Grid item xs={9}>
        <div className={classes.wrapper}>
          <div className={classes.versionFormWrapper}>
            <SchematicVersionForm
              versionsList={versionsList}
              onChange={onSchematicVersionChange}
              selectedSchematicVersion={selectedSchematicVersion}
            />
            {shouldShowButtons ? (
              <Button
                className={classes.button}
                size="small"
                color="primary"
                variant="contained"
                title="Add component"
                onClick={() => setIsDrawerOpen(true)}
              >
                <AddIcon />
                Add
              </Button>
            ) : null}
            {!shouldShowButtons && isLatestVersion ? (
              <Button
                className={`${classes.button} ${classes.editButton}`}
                size="small"
                color="primary"
                variant="contained"
                title="Edit Schematic"
                onClick={() => setEditMode(true)}
              >
                <EditIcon />
                Edit Schematic
              </Button>
            ) : null}
          </div>
          {versionCreator ? (
            <Grid className={classes.avatarWrapper}>
              <UserAvatar
                avatar={versionCreator.avatar}
                initials={versionCreator.initials}
              />
              <Typography className={classes.versionCreator}>
                {versionCreator.fullName}
              </Typography>
            </Grid>
          ) : null}
          <div className={classes.tableWrapper}>
            <SchematicTable
              onRowClick={handleClick}
              data={data}
              onEdit={onEdit}
              onDelete={onDelete}
              lengthUnit={length}
              widthUnit={width}
              isEdit={shouldShowButtons}
            />
          </div>
          <Grid item className={classes.versionCommentFormWrapper}>
            {versionsEmpty && !hasData ? null : (
              <SchematicCommentForm
                handleSubmit={versionFormSubmit}
                handleCancel={onCancel}
                initialValues={{ comment }}
                isEdit={shouldShowButtons}
                isFetching={isFetching}
              />
            )}
          </Grid>
        </div>
      </Grid>
      <SchematicDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setRowIndex(null);
        }}
      >
        <SchematicForm
          handleSubmit={formSubmit}
          initialValues={initialValues}
          lengthUnit={length}
          widthUnit={width}
          allElements={allElements}
        />
      </SchematicDrawer>
      <SchematicModal
        componentName={rowIndex !== null && data[rowIndex].name}
        isOpen={isModalOpen}
        onCancel={onCancelConfirm}
        onDelete={onDeleteConfirm}
      />
    </main>
  );
};

const styles = (theme) => ({
  emptyStateWrapper: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  emptyStateColor: {
    color: theme.palette.secondary.light,
  },
  warningIcon: {
    fontSize: theme.typography.h2.fontSize,
    paddingBottom: theme.spacing(2),
  },
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  avatarWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: `${theme.spacing(2)}px 0`,
  },
  versionCreator: {
    paddingLeft: `${theme.spacing(2)}px`,
  },
  tableWrapper: { maxHeight: '70%', overflowY: 'auto' },
  versionFormWrapper: {
    display: 'flex',
    aligntems: 'center',
    justifyContent: 'space-between',
  },
  versionCommentFormWrapper: { marginTop: 'auto', flexBasis: 'auto' },
  button: {
    marginBottom: theme.spacing(2),
    marginLeft: 'auto',
  },
  editButton: {
    display: 'flex',
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
    whiteSpace: 'nowrap',
  },
});

export default withStyles(styles)(WellBoreSchematic);
